import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApp } from "@/context/AppContext";
import { formatMoney } from "@/lib/mockData";
import { ArrowUpRight, Trash2, Inbox, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS_ORDER = ["draft", "submitted", "under_review", "approved"];
const STATUS_LABEL = { draft: "Draft", submitted: "Signed", under_review: "Filed · in review", approved: "Approved", denied: "Denied", needs_more_info: "Needs info" };

export default function Applications() {
    const { applications, deleteApplication } = useApp();

    const byStatus = STATUS_ORDER.reduce((m, s) => ({ ...m, [s]: [] }), {});
    applications.forEach((a) => {
        // map any extra statuses (denied, needs_more_info) into 'under_review' column for kanban view
        const col = STATUS_ORDER.includes(a.status) ? a.status : "under_review";
        byStatus[col].push(a);
    });

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this draft?")) return;
        try { await deleteApplication(id); toast.success("Deleted"); }
        catch { toast.error("Couldn't delete"); }
    };

    if (applications.length === 0) {
        return (
            <div className="px-5 sm:px-8 py-16 max-w-3xl mx-auto">
                <Card className="p-12 text-center border-dashed border-border" data-testid="applications-empty-state">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary mx-auto">
                        <Inbox className="h-6 w-6 text-muted-foreground" />
                    </span>
                    <h2 className="mt-5 font-serif text-3xl">No applications yet</h2>
                    <p className="mt-3 text-muted-foreground max-w-md mx-auto">When you start an application, it'll appear here so you can track progress, answer follow-ups, and e-sign when ready.</p>
                    <div className="mt-6 flex justify-center gap-2">
                        <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Link to="/app/grants">Browse grants</Link></Button>
                        <Button asChild variant="outline" className="rounded-full"><Link to="/app/claims">See money owed</Link></Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-7xl mx-auto">
            <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Tracker</p>
                <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Applications</h1>
                <p className="mt-3 text-muted-foreground max-w-2xl">{applications.length} packet{applications.length === 1 ? "" : "s"} in flight. Move each forward as you answer questions and e-sign.</p>
            </div>

            <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                {STATUS_ORDER.map((s) => (
                    <div key={s} className="rounded-2xl bg-secondary/40 border border-border p-4 flex flex-col gap-3 min-h-[400px]">
                        <div className="flex items-center justify-between">
                            <StatusPill status={s} />
                            <span className="text-xs text-muted-foreground">{byStatus[s].length}</span>
                        </div>
                        <div className="flex flex-col gap-3">
                            {byStatus[s].length === 0 ? (
                                <p className="text-xs text-muted-foreground italic py-6 text-center">No items</p>
                            ) : byStatus[s].map((app) => (
                                <AppItem key={app.id} app={app} onDelete={() => handleDelete(app.id)} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        draft: "bg-muted text-foreground",
        submitted: "bg-primary/10 text-primary",
        under_review: "bg-warning/15 text-warning-foreground",
        approved: "bg-success/15 text-success",
    };
    return <span className={cn("text-[10px] font-mono uppercase tracking-[0.18em] rounded-full px-3 py-1", map[status])}>{STATUS_LABEL[status]}</span>;
}

function AppItem({ app, onDelete }) {
    const isSigned = app.status === "submitted" || app.status === "under_review" || app.status === "approved";
    const isFiled = ["under_review", "approved", "denied", "needs_more_info"].includes(app.status);
    return (
        <Card className="p-4 border-border bg-card" data-testid={`application-card-${app.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{app.type}</p>
                    <p className="font-serif text-base mt-1 leading-tight line-clamp-2">{app.target_title}</p>
                </div>
                {!isSigned && (
                    <button onClick={onDelete} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`application-delete-${app.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            <div className="mt-3">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Progress</span><span>{app.progress}%</span>
                </div>
                <Progress value={app.progress} className="h-1" />
            </div>
            {app.confirmation_number && (
                <div className="mt-3 px-2 py-1.5 rounded-md bg-success/10 text-success text-[10px] font-mono" data-testid={`application-confirmation-${app.id}`}>
                    {app.confirmation_number}
                </div>
            )}
            <div className="mt-3 flex items-center justify-between">
                <p className="font-serif text-lg">{formatMoney(app.target_amount || 0, { compact: true })}</p>
                {isFiled && <span className="text-xs text-success inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Filed</span>}
                {isSigned && !isFiled && <span className="text-xs text-warning inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Signed</span>}
                <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
                    <Link to={`/app/detail/${app.type}/${app.target_id}`}>Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" /></Link>
                </Button>
            </div>
        </Card>
    );
}
